import type { forEach } from 'lodash';
declare const ENVIRONMENT: string;
import {
  CloneCreator,
  CreateThumbnail,
  Elements,
  ElementsManager,
  FetchData,
  MediaLibrary,
  MediaPreview,
  MediaState,
  MediaUIManager,
  MemberStack,
  NotificationMananger,
  UserManager,
  VideoPlayer,
} from './cloneme-classes';
import { type ImageListItem, MediaType } from './cloneme-classes/MediaType';
type MappedImage = {
  id: number; // Change this to 'string' if the IDs you are dealing with are always strings.
  videoURL: string;
  thumbnail: string;
  deleted?: boolean;
  imageURL: string | undefined;
};

export class CloneMe {
  private userManager: UserManager | undefined;
  //preloader: HTMLElement | null | undefined;
  mappedImages: any;
  activeImageURL: string | undefined;
  activeCloneID: string | undefined;
  activeMediaID: string | undefined;
  activeMediaType: MediaType | undefined;
  talkVideoScript: string | undefined;
  memberStackInstance: MemberStack = new MemberStack();
  imageLibrary: MediaLibrary | undefined;
  private notify: NotificationMananger = new NotificationMananger();
  elementsManager: ElementsManager = new ElementsManager();
  elements: Elements = new Elements();
  mediaPreview: MediaPreview = new MediaPreview();
  private videoPlayer: VideoPlayer | undefined;
  cloneIntro: CloneCreator | undefined;
  private uiManager: MediaUIManager | undefined;
  constructor() {
    if (this.elements.get('buttonActivate')) {
      this.uiManager = new MediaUIManager();
      this.uiManager.setMediaState(MediaState.NothingSelected);
    }
    console.log('ENVIRONMENT', ENVIRONMENT);
  }
  public async init(): Promise<void> {
    await this.initializeUser();
  }
  private async initializeUser(): Promise<void> {
    this.userManager = await UserManager.getInstance();

    try {
      this.talkVideoScript = `Hi ${this.userManager.userName}, this is the first step in developing your CloneMe DigitalDNA twin. Now let’s clone your voice so I can sound more like you. `;
      this.notify.show(`Logged in as ${this.userManager.userName}`);
      this.createIntroClone();
      this.showStrapiVideoLibrary(false, true);
      this.showImageLibrary();
      this.addEventListeners();
    } catch (error) {
      console.error('Error while creating chat:', error);
    }
  }

  private createIntroClone(): void {
    const videoPlayers = this.elements.getAll('videoPlayer');

    const player1 = new VideoPlayer({
      videoContainer: videoPlayers[0] as HTMLElement,
      onEndCallback: () => {
        console.log('Video finished playing.');
      },
    });
    this.videoPlayer = player1;
    if (videoPlayers[1]) {
      const player2 = new VideoPlayer({
        videoContainer: videoPlayers[1] as HTMLElement,
        onEndCallback: () => {
          console.log('Video finished playing.');
        },
      });
    }
    if (videoPlayers[2]) {
      const player3 = new VideoPlayer({
        videoContainer: videoPlayers[2] as HTMLElement,
        onEndCallback: () => {
          console.log('Video finished playing.');
        },
      });
    }
  }

  private hideCloneIntro(): void {
    this.videoPlayer?.hideContainer();
  }
  public addEventListeners(): void {
    this.addMediaItemSelectedListener();
    this.addDeleteButtonListener();
    this.addActivateButtonListener();
    this.addCreateCloneButtonListener();
    this.addImageUploadListeners();
  }

  private addMediaItemSelectedListener(): void {
    document.addEventListener('mediaItemSelected', (e) => {
      const customEvent = e as CustomEvent<{
        videoURL: string;
        imageURL: string;
        id: string;
        s3Link: string;
        thumbnail: string;
      }>;
      this.handleMediaItemSelected(customEvent);
    });
  }

  private handleMediaItemSelected(customEvent: CustomEvent): void {
    this.hideCloneIntro();

    if (customEvent.detail && customEvent.detail.videoURL !== 'none') {
      this.activeMediaType = MediaType.Video;
      this.mediaPreview.previewVideo(customEvent.detail.videoURL);
      this.activeMediaID = customEvent.detail.id;

      if (this.activeCloneID === this.activeMediaID) {
        this.uiManager?.setMediaState(MediaState.ActiveClonePreview);
      } else {
        this.uiManager?.setMediaState(MediaState.PreviewingVideo);
      }
    } else {
      this.activeMediaType = MediaType.Image;
      this.activeImageURL = customEvent.detail.imageURL;
      this.activeMediaID = customEvent.detail.id;
      this.mediaPreview.preview(customEvent.detail.imageURL);
      this.uiManager?.setMediaState(MediaState.PreviewingImage);
    }
  }

  private addDeleteButtonListener(): void {
    const { buttonDelete } = this.elementsManager.elements;
    if (buttonDelete) {
      buttonDelete.addEventListener('click', this.handleDeleteButtonClick.bind(this));
    }
  }

  private handleDeleteButtonClick(): void {
    if (this.activeMediaID && this.activeMediaType) {
      this.deleteMedia(this.activeMediaID, this.activeMediaType);
    } else {
      console.error('activeMediaID or activeMediaType is undefined');
    }
  }

  private addActivateButtonListener(): void {
    const buttonActivate = this.elements.get('buttonActivate');
    if (buttonActivate) {
      buttonActivate.addEventListener('click', this.handleActivateButtonClick.bind(this));
    }
  }

  private handleActivateButtonClick(): void {
    if (this.activeMediaID && this.activeMediaType === MediaType.Video) {
      const thumbnailActivated = document.querySelector(
        `.cc-thumbnail[data-id="${this.activeMediaID}"]`
      ) as HTMLElement;

      const imageId = thumbnailActivated.getAttribute('data-image-id');
      if (imageId) {
        this.userManager.setAvatarID(parseInt(imageId));
        this.activateClone(imageId);
      }
    }
  }

  private activateClone(imageId: string): void {
    FetchData.setCloneAvatar(
      imageId,
      this.userManager.getInfluencerID(),
      this.userManager.getDigitalCloneID()
    )
      .then(() => {
        this.showStrapiVideoLibrary();
        this.uiManager?.setMediaState(MediaState.ActiveClonePreview);
      })
      .catch((error) => {
        console.error('Error while creating clone:', error);
      });
  }

  private addCreateCloneButtonListener(): void {
    const { buttonCreateClone } = this.elementsManager.elements;
    if (buttonCreateClone) {
      buttonCreateClone.addEventListener('click', this.createClone.bind(this));
    }
  }

  private addImageUploadListeners(): void {
    const { hiddenFileInput, uploadButtonMediaLibrary } = this.elementsManager.elements;
    const uploadButton = document.querySelector('[cc-element="upload-button"]');

    if (hiddenFileInput instanceof HTMLInputElement) {
      hiddenFileInput.addEventListener('change', () => {
        this.uploadImage(hiddenFileInput);
      });
    }

    [uploadButton, uploadButtonMediaLibrary].forEach((btn) => {
      if (btn) {
        btn.addEventListener('click', () => {
          hiddenFileInput?.click();
        });
      }
    });
  }

  /* generateClone */
  public createClone(): void {
    // DID THE USER ALREADY CREATE A CLONE FROM THAT IMAGE?
    const lookForDupes = document.querySelectorAll(
      `.cc-thumbnail[data-image-id="${this.activeMediaID}"]`
    ) as NodeListOf<HTMLElement>;
    if (lookForDupes.length > 0) {
      this.notify.show('You already created a clone from this image. Please select another image.');
      return;
    }

    this.notify.showLoading('Creating Clone Please Wait..');
    if (!this.activeImageURL) {
      this.notify.show('Please select image');
      return;
    }

    if (this.talkVideoScript && this.activeMediaID) {
      FetchData.createClone(this.activeImageURL, this.talkVideoScript, this.activeMediaID)
        .then((talksData) => {
          this.refreshVideoLibrary();
        })
        .catch((error) => {
          console.error('Error while creating clone:', error);
        });
    }
  }

  /*UPLOAD IMAGE */
  public uploadImage(input: HTMLInputElement): void {
    this.hideCloneIntro();
    this.uiManager?.setMediaState(MediaState.UploadingImage);
    this.notify.showLoading('Uploading image please wait');

    FetchData.uploadImage(input)
      .then((response) => {
        if (this.imageLibrary) {
          this.showImageLibrary();
          this.notify.showLoadingDone('Uploading Done');
        }
      })
      .catch((error) => {
        this.notify.show(error);
        console.error('There was a problem with the fetch operation:', error.message);
      });
  }

  /* Delete Media */
  public deleteMedia(_id: string, _type: MediaType): void {
    if (_type === MediaType.Video) {
      this.deleteVideo(_id);
    } else {
      this.deleteImage(_id);
    }
    this.mediaPreview.clear();
  }
  /*DELETE VIDEO */
  public deleteVideo(id: string): void {
    console.log('deleteVideo', id);
    FetchData.deleteVideo(id)
      .then((response) => {
        console.log('videos', response);
        this.showVideoLibrary();
      })
      .catch((error) => {
        console.error('There was a problem with the fetch operation:', error.message);
      });
  }

  /*DELETE VIDEO */
  public deleteDIDVideo(id: string): void {
    console.log('delete clone from D-ID', id);
    FetchData.deleteVideoDID(id)
      .then((response) => {
        console.log('videos', response);
        //  this.showVideoLibrary();
        console.log('clone deleted from D-ID', id);
      })
      .catch((error) => {
        console.error('There was a problem with the D-ID delete fetch operation:', error.message);
      });
  }

  public deleteImage(id: string): void {
    console.log('deleteImage', id);
    FetchData.deleteImage(id)
      .then((response) => {
        console.log('videos', response);
        this.uiManager?.setMediaState(MediaState.DeletingImage);
        this.showImageLibrary();
      })
      .catch((error) => {
        console.error('There was a problem with the fetch operation:', error.message);
      });
  }

  public async showImageLibrary(): Promise<void> {
    try {
      const images = await FetchData.fetchImages();
      this.mappedImages = this.mapImages(images);

      const { imagesLibraryContainer } = this.elementsManager.elements;
      if (imagesLibraryContainer) {
        this.imageLibrary = new MediaLibrary(imagesLibraryContainer);
        this.imageLibrary.init(this.mappedImages);
      }

      const state = this.uiManager?.getState();
      if (
        state?.currentState === MediaState.DeletingImage ||
        state?.currentState === MediaState.UploadingImage
      ) {
        this.imageLibrary?.setHighlighted(this.mappedImages[0].id);
      }
    } catch (error: unknown) {
      console.error('There was a problem with the fetch operation:', 'FetchData.fetchImages');
    }
  }

  private mapImages(imagesList: any[]): MappedImage[] {
    return imagesList
      .filter(
        (item) =>
          item?.attributes?.src?.data?.attributes?.formats?.thumbnail?.url &&
          item?.attributes?.src?.data?.attributes?.url
      )
      .map((item) => ({
        id: parseInt(item.id),
        videoURL: 'none',
        thumbnail: item?.attributes?.src?.data?.attributes?.formats?.thumbnail?.url || '',
        imageURL: item?.attributes?.src?.data?.attributes?.url || '',
      }));
  }

  /* REFRESH VIDEO LIBRARY */
  public refreshVideoLibrary(): void {
    const MAX_RETRIES = 50; // Define how many times you want to retry
    const INTERVAL = 5000; // 5 seconds, adjust as needed
    let retries = 0;
    const pollForResultURL = () => {
      FetchData.fetchVideos()
        .then((videos) => {
          if (videos.length === 0) {
            return;
          }
          const firstVideo = videos[0];

          if (firstVideo && firstVideo.result_url) {
            this.notify.showLoadingDone('Clone Created!');
            FetchData.uploadVideoToStrapi(
              firstVideo.result_url,
              firstVideo.id,
              this.activeMediaID || 'none'
            )
              .then((response) => {
                if (this.imageLibrary) {
                  this.showStrapiVideoLibrary(true);
                  this.notify.showLoadingDone('Creation Done');
                  this.mediaPreview.previewVideo(firstVideo.result_url);
                  //this.deleteDIDVideo(firstVideo.id);
                }
              })
              .catch((error) => {
                this.notify.show(error);
                console.error('There was a problem with the fetch operation:', error.message);
              });
          } else if (retries < MAX_RETRIES) {
            retries++;
            setTimeout(pollForResultURL, INTERVAL);
          } else {
            console.error('Max retries reached without finding result_url for the first video.');
          }
        })
        .catch((error) => {
          if (error.message) this.notify.showLoading(error.message);
          console.error('There was a problem with the fetch operation:', error.message);
        });
    };

    pollForResultURL(); // Start polling
  }
  public showStrapiVideoLibrary(setFirstVideoActive = false, activateVideo = false): void {
    FetchData.fetchStrapiVideos()
      .then((videos) => {
        const filteredVideos = videos.filter(
          (video) => video.attributes?.src?.data !== null && video.attributes?.image !== null
        );

        const mappedVideos = filteredVideos.map((video) => {
          const { image } = video.attributes;
          const imageID = image.data.id;
          if (image) {
            console.log('image id used for avatar', imageID);
          }

          return {
            id: video.id,
            videoURL: video.attributes.src.data[0].attributes.url,
            imageURL: image.data?.attributes?.src?.data?.attributes?.url,
            thumbnail: image.data?.attributes?.src?.data?.attributes?.formats?.thumbnail?.url,
            imageID: imageID,
          };
        });

        // Assuming MediaLibrary accepts the mapped videos in its constructor or a setter method:
        const { videoLibraryContainer } = this.elementsManager.elements;
        if (videoLibraryContainer) {
          const videoLibrary = new MediaLibrary(videoLibraryContainer);
          if (videoLibrary) {
            videoLibrary.init(mappedVideos);
            if (setFirstVideoActive) {
              videoLibrary.setHighlighted(mappedVideos[0].id);
            }
            if (this.userManager.avatarID) {
              this.uiManager?.setStep2Active(true);
              const thumbnail = CreateThumbnail.setSelectedThumbnail(
                this.userManager.avatarID.toString()
              );
              if (thumbnail && activateVideo) {
                const event = new CustomEvent('mediaItemSelected', {
                  detail: {
                    videoURL: thumbnail.getAttribute('data-video-url'),
                    imageURL: thumbnail.getAttribute('data-image-url'),
                    s3Link: thumbnail.getAttribute('data-s3-url'),
                    id: thumbnail.getAttribute('data-id'),
                    thumbnail: thumbnail.getAttribute('data-thumbnail'),
                  },
                });
                document.dispatchEvent(event);
                const event2 = new CustomEvent('imageUrlChanged', {
                  detail: { imageUrl: thumbnail.getAttribute('data-s3-url') },
                });
                document.dispatchEvent(event2);
              }
            }
          }
        }
      })
      .catch((error) => {
        console.error('There was a problem with the fetch operation:', error.message);
      });
  }
  public showVideoLibrary(): void {
    FetchData.fetchVideos()
      .then((videos) => {
        const existingImageIDs = this.mappedImages.map((image: { id: any }) => image.id);

        const filteredVideos = videos.filter((video) => {
          const isMatch = existingImageIDs.includes(Number(video.name));
          return isMatch;
        });

        const mappedVideos = filteredVideos.map((video) => {
          let adjustedVideoUrl = video.result_url;

          if (adjustedVideoUrl) {
            adjustedVideoUrl = video.result_url.replace(
              's3://d-id-talks-persistent-prod/',
              'https://talks-api-results.d-id.com/'
            );
          }

          return {
            id: video.id,
            videoURL: adjustedVideoUrl,
            imageURL: video.source_url.replace(
              'https://d-id-talks-persistent-prod.s3.us-west-2.amazonaws.com/',
              'https://talks-api-results.d-id.com/'
            ),
            name: video.title,
          };
        });

        // Assuming MediaLibrary accepts the mapped videos in its constructor or a setter method:
        const { videoLibraryContainer } = this.elementsManager.elements;
        if (videoLibraryContainer) {
          const videoLibrary = new MediaLibrary(videoLibraryContainer);
          if (videoLibrary) {
            console.log('mappedVideos', mappedVideos);
            videoLibrary.init(mappedVideos);
          }
        }
      })
      .catch((error) => {
        console.error('There was a problem with the fetch operation:', error.message);
      });
  }
}
